/** The Terraform variable that turns on the installer's own strict check (MP-3693). */
export const BOUNDARY_REQUIRED_VARIABLE = 'iam_permissions_boundary_required';
/**
 * What the account enforces, from the access stack's two outputs.
 *
 * `required` means something only under `DeployPolicy=scoped`: under
 * `administrator` the deploy role is `AdministratorAccess` and the bounded
 * role policy is never created, so the account enforces nothing whatever the
 * other parameter says. A stack below v11 publishes no `DeployRoleBoundary`,
 * which is `none` — it cannot require one.
 */
export const effectiveBoundaryMode = (facts) => {
    if (facts.bootstrapUnreadable === true)
        return 'unknown';
    return facts.deployPolicy === 'scoped' && facts.deployRoleBoundary === 'required'
        ? 'required'
        : 'none';
};
/**
 * Statuses with nothing in the account to bound. A destroyed instance has no
 * roles; a never-applied one has not created any, and under `required` it
 * records its boundary before its first apply (IM-192 slice 2).
 */
const NOTHING_TO_BOUND = new Set(['destroyed', 'never-applied']);
/**
 * The "Permissions boundaries" step's answer, from this control plane's own
 * records and the access stack's outputs.
 *
 * Whether each instance's roles *actually* carry the boundary is a separate,
 * deliberate read (`roleBoundaries`), because it is a call per role; a
 * recorded boundary is what every run sends, so it is what makes an apply
 * under `required` succeed.
 */
export const boundaryCoverage = (mode, scoped, instances) => {
    const counted = instances.filter((i) => !NOTHING_TO_BOUND.has(i.status));
    const bounded = counted.filter((i) => i.bounded).map((i) => i.deploymentId);
    const unbounded = counted.filter((i) => !i.bounded).map((i) => i.deploymentId);
    const canRequire = scoped && mode === 'none' && unbounded.length === 0;
    const detail = mode === 'unknown'
        ? 'Could not read the access stack, so whether boundaries are required is unknown.'
        : mode === 'required'
            ? `Every IAM role a run creates or changes must carry its deployment’s boundary. ${String(bounded.length)} of ${String(counted.length)} instance${counted.length === 1 ? '' : 's'} with roles ${counted.length === 1 ? 'has' : 'have'} one recorded.`
            : !scoped
                ? 'Requiring boundaries needs DeployPolicy=scoped: an administrator deploy role cannot be held to one.'
                : unbounded.length > 0
                    ? `${String(unbounded.length)} of ${String(counted.length)} instance${counted.length === 1 ? '' : 's'} with roles ${unbounded.length === 1 ? 'has' : 'have'} no boundary recorded (${unbounded.join(', ')}). Record each on its own screen, then this account can require them.`
                    : `Every instance with roles has its boundary recorded (${String(bounded.length)}). The access template can now require them.`;
    return { mode, scoped, bounded, unbounded, canRequire, detail };
};
/**
 * Why a run cannot start in a `required` account, if it cannot (part 4).
 *
 * Only an `apply` creates roles. A plan creates nothing, an output reads, and a
 * destroy deletes — none is held. An apply with no boundary recorded would be
 * refused by the installer after its plan (MP-3693 AC5b) or by IAM partway
 * through; saying so here, before a run is written, costs nobody a container.
 */
export const requiredBoundaryProblem = (command, mode, recorded) => {
    if (mode !== 'required' || command !== 'apply' || recorded)
        return undefined;
    return 'This account requires a permissions boundary on every IAM role a run creates (DeployRoleBoundary=required), and this instance has none recorded. Record its boundary first: on the instance’s screen, or for a new instance on the run form, before its first apply.';
};
/**
 * Whether a run's own `iam_permissions_boundary_required` may stand.
 *
 * Set by this control plane, like the boundary's ARN: `true` exactly when the
 * account requires boundaries and the instance has one, so the installer
 * stops before applying against a missing or stale boundary (MP-3693). A run
 * may send the same value back — a refilled form does — and nothing else.
 */
export const requiredVariableProblem = (sent, expected) => {
    if (sent === undefined || sent === null || sent === '')
        return undefined;
    if (sent === expected)
        return undefined;
    return expected
        ? `${BOUNDARY_REQUIRED_VARIABLE} is set by this control plane from the account's mode, and this account requires boundaries; a run cannot turn it off.`
        : `${BOUNDARY_REQUIRED_VARIABLE} is set by this control plane from the account's mode, never by a run.`;
};
//# sourceMappingURL=required.js.map